interface KalmanFilterState {
  lat: number;
  lng: number;
  variance: number;
}

export class GPSKalmanFilter {
  private state: KalmanFilterState | null = null;
  private readonly processVariance = 0.00001;
  private readonly minAccuracy = 10;

  filter(lat: number, lng: number, accuracy: number): { lat: number; lng: number } {
    const actualAccuracy = Math.max(accuracy || this.minAccuracy, this.minAccuracy);

    // Discard very inaccurate readings
    if (actualAccuracy > 50) {
      return this.state ? { lat: this.state.lat, lng: this.state.lng } : { lat, lng };
    }

    if (this.state === null) {
      this.state = {
        lat,
        lng,
        variance: (actualAccuracy * actualAccuracy) / 1000000,
      };
      return { lat, lng };
    }

    const prediction = {
      lat: this.state.lat,
      lng: this.state.lng,
      variance: this.state.variance + this.processVariance,
    };

    const measurementVariance = (actualAccuracy * actualAccuracy) / 1000000;
    const kalmanGain = prediction.variance / (prediction.variance + measurementVariance);

    this.state = {
      lat: prediction.lat + kalmanGain * (lat - prediction.lat),
      lng: prediction.lng + kalmanGain * (lng - prediction.lng),
      variance: (1 - kalmanGain) * prediction.variance,
    };

    return { lat: this.state.lat, lng: this.state.lng };
  }

  reset(): void {
    this.state = null;
  }
}

/** Haversine distance between two GPS coordinates in kilometres. */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
